import { useNavigate } from "react-router-dom";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { OrdersList } from "~/routes/__app.collect.orders/basket/orders-list";
import { Button } from "~/components";
import { useGetCollectOrder } from "~/api/collect/hooks/use-get-collect-order";
import { T } from "@repo/transifex";
import { ErrorDisplay } from "~/components/collect/error-display";
import { employeeActions } from "~/providers/store/employee";
import { useCollectTillContext } from "~/routes/__app.collect/collect-till-context";
import { useUser } from "~/api/users/hooks/use-user";

import { Basket } from "./basket";
import { CollectBasketLayout } from "./layout";
import { TabsSkeleton } from "./tabs-skeleton";

type OrdersBasketProps = {
  childId: string;
  groupName: string;
};

export const OrdersBasketContainer = ({
  childId,
  groupName
}: OrdersBasketProps) => {
  const navigate = useNavigate();

  const { status: userStatus } = useUser({ childId });
  const { data: order, status } = useGetCollectOrder({
    childId
  });

  const canCreateOrder = useCollectTillContext(
    (till) =>
      ((till.type === "collect" && till.config.hasPreOrderCreation) ||
        till.type === "collect-lite") &&
      till.config.collectionModules.some((module) => module.type === "flex")
  );

  const showEmptyList =
    status === "success" &&
    order.collectedOrderLines.length === 0 &&
    order.activeOrderLines.length === 0;

  if (status === "pending" || userStatus === "pending") {
    return (
      <CollectBasketLayout
        tabs={<TabsSkeleton />}
        footer={
          <>
            <Button disabled size="xl" variant="skeleton" />
            <Button disabled size="xl" variant="skeleton" />
          </>
        }
      >
        <OrdersList isLoading />
      </CollectBasketLayout>
    );
  }

  if (showEmptyList || !order) {
    return (
      <CollectBasketLayout>
        <ErrorDisplay>
          <ErrorDisplay.Description className="font-normal text-interface-neutral-5">
            <T _str="No order placed today." />
          </ErrorDisplay.Description>

          <>
            {canCreateOrder ? (
              <Button
                onClick={() => {
                  employeeActions.setAuthEmployee({
                    employeeChildId: childId,
                    groupName
                  });
                  navigate(
                    `/collect/orders/create?childId=${childId}&groupName=${encodeURIComponent(
                      groupName ?? ""
                    )}`
                  );
                }}
                icon={faPlus}
                variant="neutral"
                className="rounded-full"
              >
                <T _str="Create Order" />
              </Button>
            ) : null}
          </>
        </ErrorDisplay>
      </CollectBasketLayout>
    );
  }

  return <Basket order={order} childId={childId} />;
};
